/**
 * EmployeeService
 * Service for Employee entity operations
 * Route: api/employees
 */
import { BaseService } from '../base/BaseService';
/**
 * EmployeeService
 * Handles employee CRUD operations and employee-specific queries
 * Note: Employee uses UserId (string) as primary key instead of int
 */
export class EmployeeService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? EmployeeService.DEFAULT_SERVICE_PATH,
            basePath: EmployeeService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    // ==================== GET ALL ====================
    /**
     * Get all employees with pagination
     * GET api/employees?page={page}&pageSize={pageSize}
     */
    async getAll(page = 1, pageSize = 50) {
        const response = await this.getAllRaw(page, pageSize);
        if (!response.data) {
            throw new Error('Failed to retrieve employees');
        }
        return response.data;
    }
    /**
     * Get all employees with full response
     */
    getAllRaw(page = 1, pageSize = 50) {
        const url = this.buildUrlWithParams(undefined, { page, pageSize });
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get employee by user ID
     * GET api/employees/{userId}
     */
    async getById(userId) {
        const response = await this.getByIdRaw(userId);
        if (!response.data) {
            throw new Error(`Employee ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get employee by user ID with full response
     */
    getByIdRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.get(url);
    }
    // ==================== GET BY TYPE ====================
    /**
     * Get employees by employee type with pagination
     * GET api/employees/by-type/{typeId}?page={page}&pageSize={pageSize}
     */
    async getByType(typeId, page = 1, pageSize = 50) {
        const response = await this.getByTypeRaw(typeId, page, pageSize);
        if (!response.data) {
            throw new Error(`Failed to retrieve employees by type ${typeId}`);
        }
        return response.data;
    }
    /**
     * Get employees by employee type with full response
     */
    getByTypeRaw(typeId, page = 1, pageSize = 50) {
        const endpoint = this.getEndpoint('by-type', `by-type/${typeId}`);
        const url = this.buildUrlWithParams(endpoint, { page, pageSize });
        return this.http.get(url);
    }
    // ==================== GET BY STATUS ====================
    /**
     * Get employees by employee status
     * GET api/employees/by-status/{statusId}
     */
    async getByStatus(statusId) {
        const response = await this.getByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get employees by employee status with full response
     */
    getByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('by-status', `by-status/${statusId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new employee
     * POST api/employees
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create employee');
        }
        return response.data;
    }
    /**
     * Create a new employee with full response
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing employee
     * PUT api/employees/{userId}
     */
    async update(userId, data) {
        const response = await this.updateRaw(userId, data);
        if (!response.data) {
            throw new Error(`Failed to update employee ${userId}`);
        }
        return response.data;
    }
    /**
     * Update an existing employee with full response
     */
    updateRaw(userId, data) {
        const url = this.buildUrl(userId);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete an employee
     * DELETE api/employees/{userId}
     */
    async delete(userId) {
        const response = await this.deleteRaw(userId);
        return response.success;
    }
    /**
     * Delete an employee with full response
     */
    deleteRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.delete(url);
    }
    // ==================== MODELVIEW ENDPOINTS ====================
    /**
     * Get employee ModelView by user ID
     * GET api/employees/view/{userId}
     */
    async getView(userId) {
        const response = await this.getViewRaw(userId);
        if (!response.data) {
            throw new Error(`Employee ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get employee ModelView by user ID with full response
     */
    getViewRaw(userId) {
        const endpoint = this.getEndpoint('view-by-id', `view/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all employee ModelViews with pagination
     * GET api/employees/view?page={page}&pageSize={pageSize}
     */
    async getAllViews(page = 1, pageSize = 50) {
        const response = await this.getAllViewsRaw(page, pageSize);
        if (!response.data) {
            throw new Error('Failed to retrieve employee ModelViews');
        }
        return response.data;
    }
    /**
     * Get all employee ModelViews with full response
     */
    getAllViewsRaw(page = 1, pageSize = 50) {
        const endpoint = this.getEndpoint('view-all', 'view');
        const url = this.buildUrlWithParams(endpoint, { page, pageSize });
        return this.http.get(url);
    }
    /**
     * Get employee ModelViews by type
     * GET api/employees/view/by-type/{typeId}
     */
    async getViewsByType(typeId) {
        const response = await this.getViewsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get employee ModelViews by type with full response
     */
    getViewsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('view-by-type', `view/by-type/${typeId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get employee ModelViews by status
     * GET api/employees/view/by-status/{statusId}
     */
    async getViewsByStatus(statusId) {
        const response = await this.getViewsByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get employee ModelViews by status with full response
     */
    getViewsByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('view-by-status', `view/by-status/${statusId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
EmployeeService.DEFAULT_SERVICE_PATH = 'hr';
EmployeeService.BASE_PATH = 'api/employees';
//# sourceMappingURL=EmployeeService.js.map